/**
 * Blueprints API Service
 *
 * Cloud-native configuration snapshots (digital twin) stored in Postgres JSONB.
 */

import { api } from './api';

export type BlueprintSummary = Record<string, number>;

export interface BlueprintMeta {
  id: string;
  org_id: string;
  site_id: string;
  name: string;
  description?: string | null;
  tags?: string[];
  revision?: number | null;
  payload_sha256?: string | null;
  payload_size_bytes?: number | null;
  payload_summary?: BlueprintSummary | null;
  payload_manifest?: any | null;
  created_at?: string | null;
  created_by_type?: string | null;
  created_by_id?: string | null;
  created_by_username?: string | null;
}

export interface BlueprintListResponse {
  ok: boolean;
  org_id: string;
  site_id: string;
  count: number;
  blueprints: BlueprintMeta[];
}

export interface BlueprintCreateRequest {
  name: string;
  description?: string;
  tags?: string[];
  org_id?: string;
  site_id?: string;
}

export interface BlueprintCreateResponse {
  ok: boolean;
  id: string;
  org_id: string;
  site_id: string;
  name: string;
  description?: string | null;
  tags?: string[];
  revision?: number;
  payload_sha256?: string;
  payload_size_bytes?: number;
  created_at?: string;
  summary?: BlueprintSummary;
  manifest?: any;
}

export interface BlueprintRestoreRequest {
  dry_run?: boolean;
  apply?: boolean;
  restore_mode?: 'merge';
}

export interface BlueprintRestoreResponse {
  ok: boolean;
  blueprint_id: string;
  restore_id: string;
  created_at?: string;
  summary: any;
}

export interface BlueprintCompareResponse {
  ok: boolean;
  org_id: string;
  site_id: string;
  from_id: string;
  to_id: string;
  from_revision?: number | null;
  to_revision?: number | null;
  per_entity: Record<string, { added: number; removed: number; modified: number }>;
  by_device_counts: Record<string, { added: number; removed: number; modified: number }>;
  by_device: any;
  changes: any;
}

export const blueprintsApi = {
  list: async (orgId: string = 'default', siteId: string = 'default'): Promise<BlueprintListResponse> => {
    const response = await api.get('/api/v1/blueprints', { params: { org_id: orgId, site_id: siteId } });
    return response.data;
  },

  create: async (req: BlueprintCreateRequest): Promise<BlueprintCreateResponse> => {
    const response = await api.post('/api/v1/blueprints', req);
    return response.data;
  },

  get: async (id: string, includePayload: boolean = false): Promise<{ ok: boolean; blueprint: BlueprintMeta & { payload?: any } }> => {
    const response = await api.get(`/api/v1/blueprints/${encodeURIComponent(id)}`, {
      params: { include_payload: includePayload },
    });
    return response.data;
  },

  restore: async (id: string, req: BlueprintRestoreRequest): Promise<BlueprintRestoreResponse> => {
    const response = await api.post(`/api/v1/blueprints/${encodeURIComponent(id)}/restore`, req);
    return response.data;
  },

  compare: async (fromId: string, toId: string): Promise<BlueprintCompareResponse> => {
    const response = await api.get('/api/v1/blueprints/compare', { params: { from: fromId, to: toId } });
    return response.data;
  },

  download: async (id: string): Promise<Blob> => {
    const response = await api.get(`/api/v1/blueprints/${encodeURIComponent(id)}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};

export default blueprintsApi;

